<script setup lang="ts">
import { computed } from 'vue'

const { getLatestNews } = useNews()
const { formatDayMonthYear } = useDateFormat()
const { data: news } = await useAsyncData('home-news', () => getLatestNews(4))

const featuredNews = computed(() => news.value && news.value.length > 0 ? news.value[0] : null)
const otherNews = computed(() => news.value && news.value.length > 1 ? news.value.slice(1) : [])

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Masterclass': return 'bg-purple-500 text-white'
    case 'Événement': return 'bg-oxo-red text-white'
    default: return 'bg-oxo-yellow text-slate-900'
  }
}
</script>

<template>
  <section class="py-section-py bg-background relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10">
      
      <!-- Header -->
      <div class="flex flex-col md:flex-row justify-between items-end mb-section-header-mb">
        <UiSectionHeader 
          align="left"
          titlePrefix="DERNIÈRES"
          titleHighlight="ACTUALITÉS"
          subtitle="Restez informés de la vie de l'école et de nos événements."
          class="mb-0 overflow-visible"
        />
        <AppButton to="/actus" variant="outline" class="hidden md:inline-flex mt-6 md:mt-0">
          Voir toutes les actus
        </AppButton>
      </div>

      <div v-if="featuredNews" class="flex flex-col xl:flex-row gap-8">
        
        <!-- Featured Article (Left on Desktop, Top on Tablet/Mobile) -->
        <article class="xl:w-2/3 group relative bg-oxo-card-bg rounded-3xl overflow-hidden shadow-2xl border border-white/5 cursor-pointer aspect-video min-h-[250px]">
          <!-- Link to Article -->
          <NuxtLink :to="`/actus/${featuredNews.slug}`" class="absolute inset-0 z-30" aria-label="Lire l'article"></NuxtLink>
          
          <!-- Full Background Image -->
          <div class="absolute inset-0 w-full h-full">
            <NuxtImg loading="lazy"
              :src="featuredNews.image"
              :alt="featuredNews.title"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              sizes="100vw lg:66vw"
            />
            <div class="absolute inset-0 bg-black/60 z-20"></div>
          </div>

          <!-- Content Overlay (Bottom) -->
          <div class="relative z-20 h-full flex flex-col justify-end p-6 md:p-10">
            <!-- Badge -->
            <div class="mb-auto">
              <span 
                class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg backdrop-blur-md bg-white/10 border border-white/20 text-white"
              >
                {{ featuredNews.category }}
              </span>
            </div>

            <!-- Meta -->
            <div class="mb-2 md:mb-4 flex items-center gap-3" v-if="featuredNews.date">
              <span class="w-10 h-px bg-oxo-red"></span>
              <span class="text-white/80 text-[10px] md:text-xs font-mono uppercase tracking-widest">{{ featuredNews.date }}</span>
            </div>

            <h3 class="font-display font-black text-xl md:text-4xl text-white mb-2 md:mb-6 group-hover:text-oxo-yellow transition-colors duration-300 uppercase leading-tight max-w-2xl">
              {{ featuredNews.title }}
            </h3>
            
            <p class="text-white/80 line-clamp-1 md:line-clamp-3 mb-4 md:mb-8 text-xs md:text-base font-light leading-relaxed max-w-2xl">
              {{ featuredNews.excerpt }}
            </p>
            
            <div class="flex items-center gap-4 group/btn">
              <span class="text-white font-black text-[10px] md:text-xs uppercase tracking-[0.2em] border-b-2 border-oxo-yellow pb-1">Lire l'article</span>
              <i class="fas fa-arrow-right text-oxo-yellow transition-transform duration-300 group-hover/btn:translate-x-2" aria-hidden="true"></i>
            </div>
          </div>
        </article>

        <!-- Secondary Articles Grid (Right on Desktop, Bottom on Tablet/Mobile) -->
        <div class="xl:w-1/3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-1 gap-6">
          <article 
            v-for="item in otherNews" 
            :key="item.id"
            class="group relative flex flex-row gap-4 bg-oxo-card-bg/20 p-3 rounded-2xl hover:bg-oxo-card-bg/40 transition-all duration-300 border border-white/5 hover:border-white/10 w-full"
          >
            <NuxtLink :to="`/actus/${item.slug}`" class="absolute inset-0 z-20" aria-label="Lire l'article"></NuxtLink>
            <!-- Image (16/9 Thumbnail) -->
            <div class="w-32 lg:w-40 aspect-video shrink-0 rounded-xl overflow-hidden relative shadow-lg">
               <NuxtImg loading="lazy"
                :src="item.image"
                :alt="item.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                sizes="160px lg:200px"
              />
            </div>

            <!-- Content -->
            <div class="flex flex-col py-1 pr-2 min-w-0">
              <div class="flex items-center gap-2 mb-1.5">
                  <span class="text-[10px] font-bold uppercase text-oxo-red">{{ item.category }}</span>
                  <span class="text-foreground-dim text-[10px] font-mono">• {{ item.date }}</span>
              </div>
              <h3 class="font-display font-bold text-sm xl:text-base text-foreground mb-2 group-hover:text-oxo-red transition-colors duration-300 line-clamp-2 leading-snug">
                {{ item.title }}
              </h3>
              <div class="mt-auto">
                 <i class="fas fa-arrow-right text-oxo-yellow/50 text-xs transform -rotate-45 group-hover:rotate-0 transition-transform duration-300" aria-hidden="true"></i>
              </div>
            </div>
          </article>
        </div>

      </div>


      <div class="mt-12 text-center md:hidden">
        <AppButton to="/actus" variant="outline" class="w-full">
          Voir toutes les actus
        </AppButton>
      </div>

    </div>
  </section>
</template>
